import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import "./header.scss";
import { useTranslation } from "react-i18next";
import {
  AssessmentButton,
  useClickOutsideListenerRef,
  useOnlineStatus,
} from "ui-components";
import { assessmentDraftJson } from "../../../context/CreateAssessmentDraftJson/CreateAssessmentDraftJson";
import {
  checkActive,
  checkStringContainsSubStr,
  foundUniqueId,
  getBrowserName,
  isAssessmentActive,
  isDemo,
  playNotificationSound,
  removeJobDetailsData,
} from "../../../utils/Common/Constant";
import ProfileSettings from "./ProfileSettings";
import { getCompanyProfileDetails } from "../../../service/Scope/Scope";
import { useEffect, useState } from "react";
import Banner from "../../Banner/Banner";
import LogoTagline from "../../../utils/Images/LogoTagline";
import { SettingAppEnums } from "../../../utils/Enums/SettingAppEnums";
import NotificationPopover from "../../../pages/Notifications/NotificationPopover";
import { getNotificationCount } from "../../../service/Notification/Notification";
import { onMessage } from "firebase/messaging";
import { messaging } from "../../../PushNotification/firebase";
import { CommonEnums } from "../../../utils/Enums/CommonEnums";

const Header = () => {
  const finalData = assessmentDraftJson();
  const { tempAssessmentUId } = finalData;
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { pathname } = useLocation();
  const { tempUniqueId } = useParams();
  const email = localStorage.getItem("email");
  const companyUserId = localStorage.getItem("companyUserId");
  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const [showJobsSubmenu, setShowJobsSubmenu] = useState(false);
  const [showCreateSubmenu, setShowCreateSubmenu] = useState(false);
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const isJobsRoute = ["jobs", "job"];
  const containsJobRoute = pathSegments.some((route: string) =>
    isJobsRoute.includes(route),
  );
  const jobId = localStorage.getItem("jobId");
  const menuRef: any = useClickOutsideListenerRef(() => {
    setShowJobsSubmenu(false);
  });
  const createBtnRef: any = useClickOutsideListenerRef(() => {
    setShowCreateSubmenu(false);
  });
  const licenseWithStatus = JSON.parse(
    localStorage.getItem("licenseWithStatus") ?? "[]",
  );
  const isStaffAugEnabled = licenseWithStatus?.some(
    (item: any) =>
      item["license.name"] === SettingAppEnums.LICENSE_STAFF_AUGMENTATION &&
      item.isEnabled === true,
  );
  const getProfileDetails = async () => {
    try {
      const res: any = await getCompanyProfileDetails(companyUserId);
      if (res?.status === 200) {
        setProfileData(res?.data?.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  // get notification count
  const getNotificationCountApi = async () => {
    try {
      const res = await getNotificationCount();
      if (res?.status === 200) {
        setNotificationCount(res?.data?.count);
      }
    } catch (error) {
      console.error(error);
    }
  };
  // call count api getNotificationCountApi in every 20 sec in safari
  useEffect(() => {
    if (getBrowserName() === CommonEnums.BROWSER_SAFARI) {
      const interval = setInterval(() => {
        getNotificationCountApi();
      }, 20000);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    getProfileDetails(); // your existing call
    getNotificationCountApi();
    if (getBrowserName() !== CommonEnums.BROWSER_SAFARI) {
      // Foreground: Firebase onMessage
      const unsubscribe = onMessage(messaging, () => {
        getNotificationCountApi();
      });
      // Background: Listen for service worker messages
      if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data?.type === "NEW_NOTIFICATION") {
            getNotificationCountApi();
            playNotificationSound();
          }
        });
      }
      return () => {
        unsubscribe();
      };
    }
  }, [isOnline]);

  const toggleJobsSubmenu = () => {
    setShowJobsSubmenu((prev) => !prev);
    setShowCreateSubmenu(false);
  };

  const toggleCreateSubmenu = () => {
    setShowCreateSubmenu((prev) => !prev);
    setShowJobsSubmenu(false);
  };

  return (
    <>
      {isDemo && (
        <div className="demoBanner">
          <div className="demoBannerText">
            <div className="demoRibbon">Demo</div>
          </div>
        </div>
      )}
      {!isOnline && (
        <div className="internet-banner">
          <div className={`banner ${!isOnline ? "show zIndexTop" : ""}`}>
            {t("GENERAL.INTERNET_CONNECTION")}
          </div>
        </div>
      )}
      <nav
        className={`navbar navbar-expand-lg assessmentHeaderParent ${!isOnline && "main-content"} ${isDemo ? "mt-2" : ""} ${containsJobRoute && "jobModuleStyle"} `}
      >
        <div className="container-fluid logo-link">
          <div
            className="logo"
            onClick={() => {
              navigate(`/${currentLanguage}/jobs`, { replace: true });
            }}
          >
            <LogoTagline />
          </div>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 link">
              {/* Jobs */}
              <li className="nav-item">
                <NavLink
                  className={`nav-link ${pathname.includes("/job") || pathname.includes("/jobs") ? "addBottomBorder" : ""}`}
                  to={`/${currentLanguage}/jobs`}
                >
                  {t("GENERAL.JOBS")}
                </NavLink>
              </li>
              {isStaffAugEnabled && (
                <li className="nav-item" style={{ position: "relative" }}>
                  <div
                    className={`nav-link cursorPointer ${checkStringContainsSubStr(pathname, ["vendor", "contract", "applications"]) ? "addBottomBorder" : ""}`}
                    onClick={toggleJobsSubmenu}
                    style={{ cursor: "pointer" }}
                    ref={menuRef}
                  >
                    {t("GENERAL.STAFF_AUGMENTATION")}
                  </div>
                  <ul
                    className={`submenu ${showJobsSubmenu ? "open" : "closed"}`}
                  >
                    <li className="submenu-item">
                      <NavLink
                        className="nav-link"
                        to={`/${currentLanguage}/vendors`}
                        onClick={toggleJobsSubmenu}
                      >
                        {t("RESUME.VENDORS")}
                      </NavLink>
                    </li>
                    <li className="submenu-item">
                      <NavLink
                        className="nav-link"
                        to={`/${currentLanguage}/contracts`}
                        onClick={toggleJobsSubmenu}
                      >
                        {t("RESUME.CONTRACTS")}
                      </NavLink>
                    </li>
                    <li className="submenu-item">
                      <NavLink
                        className="nav-link"
                        to={`/${currentLanguage}/applications`}
                        onClick={toggleJobsSubmenu}
                      >
                        {t("RESUME.APPLICATIONS")}
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}
              {/* Assessment list */}
              <li className="nav-item">
                <NavLink
                  className={
                    isAssessmentActive(pathname, currentLanguage)
                      ? "nav-link addBottomBorder"
                      : "nav-link"
                  }
                  to={`/${currentLanguage}/assessment/list`}
                >
                  {t("CREATED_ASSESSMENT_LIST_AND_REVIEW.ASSESSMENT")}
                </NavLink>
              </li>
              <li className="nav-item " style={{ position: "relative" }}>
                <div ref={createBtnRef}>
                  <AssessmentButton
                    onClick={toggleCreateSubmenu}
                    className={`lightBtn ${checkActive(pathname, tempUniqueId, currentLanguage) ? "activeCreateBtn" : ""}`}
                  >
                    {t("GENERAL.CREATE")}
                  </AssessmentButton>
                </div>
                <ul
                  className={`submenu ${showCreateSubmenu ? "open" : "closed"}`}
                >
                  <li
                    className="submenu-item"
                    onClick={() =>
                      navigate(`/${currentLanguage}/job/create-job`)
                    }
                  >
                    <NavLink
                      className="nav-link"
                      to={`/${currentLanguage}/job/create-job`}
                      onClick={() => {
                        toggleCreateSubmenu();
                        removeJobDetailsData();
                        window.location.href = `/${currentLanguage}/job/create-job`;
                        removeJobDetailsData();
                      }}
                    >
                      {t("GENERAL.JOB")}
                    </NavLink>
                  </li>
                  <li
                    className="submenu-item"
                    onClick={() => {
                      toggleCreateSubmenu();
                      const editedParams = new URLSearchParams(
                        location.search,
                      ).get("edited");
                      const assessmentTime =
                        sessionStorage.getItem("totalQuestionTime") || "";
                      if (Number(assessmentTime)) {
                        localStorage.setItem("newAssessmentPopup", "visible");
                      }
                      if (editedParams) {
                        navigate(
                          `/${currentLanguage}/assessment/build/${tempUniqueId}?edited=true${jobId ? `?jobId=${jobId}` : ""}`,
                        );
                      } else {
                        navigate(
                          `/${currentLanguage}/assessment/template/${foundUniqueId(tempAssessmentUId)}${jobId ? `?jobId=${jobId}` : ""}`,
                        );
                      }
                    }}
                  >
                    <span className="nav-link">{t("GENERAL.ASSESSMENT")}</span>
                  </li>
                </ul>
              </li>
            </ul>
            <NotificationPopover
              notificationCount={notificationCount}
              getNotificationCountApi={getNotificationCountApi}
            />
            <ProfileSettings email={email} profileData={profileData} />
          </div>
        </div>
      </nav>
      {!loading && isOnline && !profileData?.isActive && <Banner />}
      <div className="borderBottomLine"></div>
    </>
  );
};

export default Header;
